import { useState } from "react"

import ReactGA from "react-ga4"
import { useTranslation } from "react-i18next"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router"
import { useParams } from "react-router-dom"

import * as Yup from "yup"
import { DialogTitle } from "@mui/material"
import Dialog from "@mui/material/Dialog"
import DialogContent from "@mui/material/DialogContent"
import Grid from "@mui/material/Grid"
import { useTheme } from "@mui/material/styles"
import useMediaQuery from "@mui/material/useMediaQuery"
import { Button, NotificationUtils, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { FormikProvider, useFormik, useFormikContext } from "formik"

import { KonanAPI } from "../../../services/KonanAPI"
import type { CreateModelRequest, CreateModelResponse } from "../../../types/custom/projects"
import type { Model } from "../../../types/generated/api/Model"
import type { ModelListCreate } from "../../../types/generated/api/ModelListCreate"
import type { PaginatedModelListCreateList } from "../../../types/generated/api/PaginatedModelListCreateList"
import { ModelCreationForm } from "../../projects/components/ModelCreationForm"
import { PostCreationDialog } from "../../projects/components/PostCreationDialog"
import { setCurrentModel } from "../Slice"
import type { CreationDialogContainerProps, CreationDialogFormikValues, CreationDialogProps } from "../interfaces"

import styles from "../Models.module.scss"

/**
 * Model creation dialog component.
 * Handles the display and logic for creating a new model within a project.
 *
 * @param  {string} projectId - The ID of the project for which the model is being created.
 * @param  {function} onClose - Callback function to handle the dialog close event.
 * @param  {boolean} isOpen - Indicates whether the dialog is open or not.
 * @param  {ModelLogs} modelLogs - Object containing model creation logs, loading state and errors.
 * @param  {function} handleFormSubmit - Callback function to handle the form submission.
 * @return  {React.ReactElement}
 */
export function CreationDialog(props: Readonly<CreationDialogProps>): React.ReactElement {
  const { projectId, onClose, modelLogs, handleFormSubmit } = props

  // Access formik context to manage form state
  const { dirty, isValid, isSubmitting } = useFormikContext<CreationDialogFormikValues>()
  const { t } = useTranslation("models")

  return (
    <Grid container direction="column" className={styles.modelCreationDialog}>
      {/* Show the post creation dialog if there are logs, loading, or errors */}
      {modelLogs.logs || modelLogs.loading || modelLogs.errors ? (
        <Grid item className={styles.imageContainer}>
          <PostCreationDialog
            creationErrors={modelLogs?.errors}
            creationLogs={modelLogs?.logs}
            isLoading={modelLogs?.loading}
          />
        </Grid>
      ) : (
        <ModelCreationForm />
      )}

      {/* Display action buttons if not loading */}
      {!modelLogs.loading && (
        <Grid container item justifyContent="flex-end" spacing={1} paddingTop={2}>
          <Grid item>
            <Button variant="secondary" onClick={onClose} disabled={isSubmitting} id="cancel">
              {!modelLogs.errors && !modelLogs.logs ? t("dialogs.creation.cancel") : t("dialogs.creation.close")}
            </Button>
          </Grid>

          {/* Show submit button only if there are no errors or logs */}
          {!modelLogs.errors && !modelLogs.logs && (
            <Grid item>
              <Button variant="primary" onClick={handleFormSubmit} disabled={!(dirty && isValid)} id="submit">
                {!modelLogs.logs && !modelLogs.errors
                  ? t("dialogs.creation.submit_create")
                  : !projectId
                    ? t("dialogs.creation.go_to_deployment")
                    : t("dialogs.creation.go_to_model")}
              </Button>
            </Grid>
          )}
        </Grid>
      )}
    </Grid>
  )
}

// Yup validation schema for form fields
const validationSchema = Yup.object({
  name: Yup.string().required("Required"),
  registry: Yup.string().required("Required"),
  image_url: Yup.string().required("Required"),
  exposed_port: Yup.number().notOneOf([8008, 9090], "Restricted port number").required("Required"),
  docker_username: Yup.string().max(50, "Must be 50 characters or less"),
  docker_password: Yup.string().max(50, "Must be 50 characters or less"),
})

/**
 * Container component for the model creation dialog.
 * Manages the state and logic for creating a new model.
 *
 * @param {CreationDialogContainerProps} props - The properties passed to the component.
 * @return {React.ReactElement} - The rendered component.
 */
export function CreationDialogContainer(props: Readonly<CreationDialogContainerProps>): React.ReactElement {
  const { isOpen, onClose, isProjectCard } = props

  const { id: projectId } = useParams<{ id: string }>()

  const queryClient = useQueryClient()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const theme = useTheme()
  const { t } = useTranslation("models")

  const [createdModel, setCreatedModel] = useState<ModelListCreate>()

  // State to manage model creation logs and errors
  const [modelLogs, setModelLogs] = useState<{
    errors?: Record<string, string>[]
    logs?: string | null
    loading: boolean
  }>({
    loading: false,
  })

  // Fetch models on mount, disable refetching in integration screen
  const { data: models, isFetching } = useQuery<AxiosResponse<PaginatedModelListCreateList>, AxiosError>(
    ["models", projectId, "state"],
    () =>
      KonanAPI.fetchModels({
        projectId: projectId as string,
        ordering: "state",
      }),
    {
      refetchOnWindowFocus: false,
    },
  )

  const isFirstModel = models?.data.count === 0

  // Mutation to handle model creation API call
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const createModelMutation = useMutation<AxiosResponse<CreateModelResponse>, AxiosError<any>, CreateModelRequest>(
    KonanAPI.createModel,
    {
      onMutate: () =>
        setModelLogs({
          loading: true,
        }),
      onSuccess: (response) => {
        setModelLogs({
          errors: response?.data?.errors,
          logs: response?.data?.container_logs,
          loading: false,
        })
      },
      onError: ({ response }) => {
        setModelLogs({
          errors: response?.data?.errors,
          logs: response?.data?.container_logs,
          loading: false,
        })

        NotificationUtils.toast(
          response?.data.name?.[0] ?? response?.data.details ?? "An error occurred, please try again later",
          {
            severity: "error",
          },
        )
      },
    },
  )

  // Formik setup for handling form state and submission
  const formik = useFormik({
    initialValues: {
      name: "",
      image_url: "",
      registry: "KCR",
      exposed_port: 8000,
      docker_username: "",
      docker_password: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      // Create new Project or Model
      try {
        ReactGA.event({
          category: "Deployments",
          action: "Creating new model",
        })
        const response = await createModelMutation.mutateAsync({
          project_uuid: projectId as string,
          name: values.name,
          image_url: values.image_url,
          exposed_port: values.exposed_port,
          docker_password: values.docker_password || undefined,
          docker_username: values.docker_username || undefined,
          state: isFirstModel ? "live" : "disabled",
        })

        setCreatedModel(response.data.model)

        queryClient.invalidateQueries(["overviewModels", projectId])
        queryClient.invalidateQueries("models")
      } catch (e) {
        ReactGA.event({
          category: "Models",
          action: "model contains errors",
        })
      }
    },
  })

  // Handle form submission and post-submission navigation
  const handleFormSubmit = (): void => {
    if (!createModelMutation.isLoading && !isFetching) {
      formik.submitForm()
    } else {
      onClose()

      if (!createdModel?.uuid) {
        navigate(`${pathname}/${projectId}/overview/`)
      } else {
        dispatch(setCurrentModel(createdModel as Model))
        if (isProjectCard) {
          navigate(`projects/${projectId}/models/requests/`)
        } else {
          navigate(`${pathname.substring(0, pathname.lastIndexOf("/"))}/models/requests/`)
        }
      }
    }
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullScreen={useMediaQuery(theme.breakpoints.down("md"))}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle className="dialog-header-base">
        <Typography variant="h3-bold">{t("dialogs.creation.title")}</Typography>
      </DialogTitle>

      <DialogContent className={`dialog-content-base ${styles.dialog}`}>
        <FormikProvider value={formik}>
          <CreationDialog
            projectId={projectId as string}
            onClose={onClose}
            modelLogs={modelLogs}
            handleFormSubmit={handleFormSubmit}
          />
        </FormikProvider>
      </DialogContent>
    </Dialog>
  )
}
